import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import BackButton from '../components/BackButton.jsx';
import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { AreaChart } from 'foundry-charts';
import 'foundry-charts/styles';
import api from '../api/client.js';
import FollowupEditor from '../components/FollowupEditor.jsx';
import LogOutreachModal from '../components/LogOutreachModal.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import SalesHistory from '../components/SalesHistory.jsx';
import CustomerScorecard from '../components/CustomerScorecard.jsx';
import ResearchBrief from '../components/ResearchBrief.jsx';

const TOUCH_ICON = {
  visit: '🚗', call: '📞', email: '✉️', quote: '💲', photo: '📷', note: '✏️',
};
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const NAVY = '#0A2240', CRIMSON = '#C8102E', GOLD = '#D4A017';

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}
function money(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const [creatingFollowup, setCreatingFollowup] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [editingProspect, setEditingProspect] = useState(false);
  const [prospectForm, setProspectForm] = useState(null);
  const [tab, setTab] = useState('activity');
  const [touchFilter, setTouchFilter] = useState('');
  const [loggingOutreach, setLoggingOutreach] = useState(false);
  const qc = useQueryClient();
  const canSeeSales = user?.role === 'superadmin' || user?.can_see_sales;

  const c = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/customers/${id}`).then(r => r.data.data),
  });
  const a = useQuery({
    queryKey: ['customer-activity', id],
    queryFn: () => api.get(`/customers/${id}/activity`).then(r => r.data.data),
  });
  // Shared with SalesHistory (same key) → one fetch; powers the trend chart.
  const sales = useQuery({
    queryKey: ['sales-history', id],
    queryFn: () => api.get(`/sales/customers/${id}/history`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });
  // Open pipeline for this customer (KPI + Pipeline tab).
  const pipeline = useQuery({
    queryKey: ['customer-pipeline', id],
    queryFn: () => api.get(`/opportunities?customer_id=${id}&status=open`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });
  // Won/lost deals for this customer — separate from `pipeline` above so the
  // open-value KPI stays open-only; shown as its own list in the Pipeline tab
  // instead of just vanishing once a deal closes.
  const closedPipeline = useQuery({
    queryKey: ['customer-pipeline-closed', id],
    queryFn: () => api.get(`/opportunities?customer_id=${id}&status=closed`).then(r => r.data.data),
    enabled: canSeeSales && tab === 'pipeline',
    retry: false,
  });
  // The customer's owned fleet (#58) — Fleet tab.
  const fleet = useQuery({
    queryKey: ['customer-units', id],
    queryFn: () => api.get(`/customers/${id}/units`).then(r => r.data.data),
    enabled: canSeeSales,
    retry: false,
  });

  const saveNotes = useMutation({
    mutationFn: (notes) => api.put(`/customers/${id}`, { notes }).then(r => r.data.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customer', id] }); setEditingNotes(false); },
  });

  // Prospects aren't Excede-synced, so unlike real customers their name +
  // contact fields are editable by anyone — no can_edit_customers gate,
  // matching the server rule (customers.js PUT /:id).
  const saveProspect = useMutation({
    mutationFn: (data) => api.put(`/customers/${id}`, data).then(r => r.data.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customer', id] }); setEditingProspect(false); },
  });

  // 24-month stacked trend (aggregate Karmak+Excede rows per month).
  const trend = useMemo(() => {
    const monthly = sales.data?.monthly;
    if (!monthly?.length) return [];
    const byMonth = {};
    for (const r of monthly) {
      const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = { key, year: r.year, month: r.month, Parts: 0, Service: 0, Trailer: 0 };
      byMonth[key].Parts   += Number(r.parts_sales   || 0);
      byMonth[key].Service += Number(r.service_sales || 0);
      byMonth[key].Trailer += Number(r.trailer_sales || 0);
    }
    return Object.values(byMonth)
      .sort((x, y) => x.key.localeCompare(y.key))
      .slice(-24)
      .map(m => ({ month: `${MONTH_ABBR[m.month - 1]} ${String(m.year).slice(2)}`, Parts: m.Parts, Service: m.Service, Trailer: m.Trailer }));
  }, [sales.data]);

  if (c.isLoading) return <div className="text-muted">Loading…</div>;
  if (!c.data) return <div className="text-crimson">Not found.</div>;

  const cust = c.data;
  const primaryContact = cust.contacts?.find(p => p.is_primary) || cust.contacts?.[0] || null;
  const stats = a.data?.stats || {};
  const lastTouchDays = daysSince(stats.last_touch_at);
  const lastVisitDays = daysSince(stats.last_visit_at);
  const openPipeValue = (pipeline.data || []).reduce((s, o) => s + Number(o.value_usd || 0), 0);

  const notes = a.data?.notes || [];
  const filteredNotes = touchFilter ? notes.filter(n => (n.touch_type || 'note') === touchFilter) : notes;

  const TABS = [
    { key: 'activity', label: `Activity (${notes.length})` },
    ...(canSeeSales ? [{ key: 'sales', label: 'Sales' }] : []),
    ...(canSeeSales ? [{ key: 'pipeline', label: `Pipeline (${pipeline.data?.length ?? 0})` }] : []),
    ...(canSeeSales ? [{ key: 'fleet', label: `Fleet (${fleet.data?.length ?? 0})` }] : []),
    { key: 'followups', label: `Follow-ups (${a.data?.followups.length ?? 0})` },
    { key: 'people', label: `People (${cust.contacts?.length ?? 0})` },
  ];

  return (
    <div className="space-y-4">
      <BackButton fallback="/customers" />
      {/* ── Header ───────────────────────────────────────────── */}
      <div>
        <div className="section-eyebrow">Customer</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-2xl">{cust.name}</h1>
          {cust.status && (
            <span className={clsx('text-[10px] uppercase tracking-wider px-2 py-0.5 rounded',
              cust.status === 'active' ? 'bg-emerald-100 text-emerald-900'
              : cust.status === 'prospect' ? 'bg-amber-100 text-amber-900'
              : 'bg-panel text-muted')}>
              {cust.status}
            </span>
          )}
          {cust.status === 'prospect' && !editingProspect && (
            <button className="text-xs text-navy hover:text-crimson"
                    onClick={() => {
                      setProspectForm({
                        name: cust.name || '', phone: cust.phone || '', email: cust.email || '',
                        address: cust.address || '', city: cust.city || '', state: cust.state || '', zip: cust.zip || '',
                      });
                      setEditingProspect(true);
                    }}>
              ✎ Edit
            </button>
          )}
        </div>
        <div className="rule-crimson" />

        {editingProspect ? (
          <div className="mt-2 card p-3 space-y-2 border-amber-300 bg-amber-50 max-w-md">
            <input className="input text-sm" placeholder="Name" value={prospectForm.name}
                   onChange={e => setProspectForm(f => ({ ...f, name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input text-sm" placeholder="Phone" value={prospectForm.phone}
                     onChange={e => setProspectForm(f => ({ ...f, phone: e.target.value }))} />
              <input className="input text-sm" placeholder="Email" value={prospectForm.email}
                     onChange={e => setProspectForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <input className="input text-sm" placeholder="Address" value={prospectForm.address}
                   onChange={e => setProspectForm(f => ({ ...f, address: e.target.value }))} />
            <div className="grid grid-cols-3 gap-2">
              <input className="input text-sm" placeholder="City" value={prospectForm.city}
                     onChange={e => setProspectForm(f => ({ ...f, city: e.target.value }))} />
              <input className="input text-sm" placeholder="State" value={prospectForm.state}
                     onChange={e => setProspectForm(f => ({ ...f, state: e.target.value }))} />
              <input className="input text-sm" placeholder="Zip" value={prospectForm.zip}
                     onChange={e => setProspectForm(f => ({ ...f, zip: e.target.value }))} />
            </div>
            {saveProspect.isError && (
              <div className="text-crimson text-xs">{saveProspect.error?.response?.data?.error || 'Save failed'}</div>
            )}
            <div className="flex gap-2">
              <button className="btn-primary text-sm" disabled={!prospectForm.name.trim() || saveProspect.isPending}
                      onClick={() => saveProspect.mutate(prospectForm)}>
                {saveProspect.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-ghost text-sm" onClick={() => setEditingProspect(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted flex flex-wrap gap-x-3">
            <span>{[cust.address, cust.city, cust.state, cust.zip].filter(Boolean).join(', ') || '—'}</span>
            {cust.account_number && <span>· #{cust.account_number}</span>}
            {cust.phone && <span>· 📞 {cust.phone}</span>}
          </div>
        )}
        {primaryContact && (
          <div className="text-sm text-navy mt-1">
            👤 {primaryContact.name}
            {primaryContact.role && <span className="text-muted"> · {primaryContact.role}</span>}
            {primaryContact.phone && <span className="text-muted"> · {primaryContact.phone}</span>}
          </div>
        )}
      </div>

      {/* ── Action buttons ───────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap">
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=visit`} className="btn-primary">🚗 Log visit</Link>
        <button className="btn-ghost" onClick={() => setLoggingOutreach(true)}>📞 Log outreach</button>
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=call`} className="btn-ghost">📞 Log call</Link>
        <Link to={`/notes/new?customer_id=${cust.id}&touch_type=email`} className="btn-ghost">✉️ Log email</Link>
        <Link to={`/notes/new?customer_id=${cust.id}`} className="btn-ghost">+ New note</Link>
        <button className="btn-ghost" onClick={() => setCreatingFollowup(true)}>⏱ + Follow-up</button>
      </div>

      {creatingFollowup && (
        <FollowupEditor initial={{ customer_id: cust.id }}
          onClose={() => setCreatingFollowup(false)}
          onSaved={() => { setCreatingFollowup(false); qc.invalidateQueries({ queryKey: ['customer-activity', id] }); }} />
      )}

      {loggingOutreach && (
        <LogOutreachModal
          customerId={cust.id}
          customerName={cust.name}
          defaultContactName={primaryContact?.name}
          onClose={() => setLoggingOutreach(false)}
          onLogged={() => qc.invalidateQueries({ queryKey: ['customer-activity', id] })}
        />
      )}

      {/* ── Dashboard band ───────────────────────────────────── */}
      {/* Relationship KPIs (always visible) */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        <Stat label="Last touch" value={lastTouchDays != null ? `${lastTouchDays}d` : 'never'}
              warn={lastTouchDays != null && lastTouchDays > 30} />
        <Stat label="Last visit" value={lastVisitDays != null ? `${lastVisitDays}d` : 'never'}
              warn={lastVisitDays != null && lastVisitDays > 60} />
        <Stat label="Total visits" value={stats.total_visits || 0} />
        <Stat label="30d touches" value={stats.touches_30d || 0} />
        {canSeeSales
          ? <Stat label="Open pipeline" value={pipeline.data?.length ? money(openPipeValue) : '—'} />
          : <Stat label="Total touches" value={stats.total_touches || 0} />}
      </div>

      {/* Revenue scorecard — leads with the over/under verdict (trailer-inclusive) */}
      {canSeeSales && <CustomerScorecard customerId={id} />}

      {/* 24-month revenue trend */}
      {canSeeSales && trend.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Revenue trend — last 24 months</div>
          <div className="rule-crimson" />
          <div className="mt-3">
            <AreaChart
              data={trend}
              xKey="month"
              series={[
                { key: 'Parts',   label: 'Parts',   color: NAVY },
                { key: 'Trailer', label: 'Trailer', color: CRIMSON },
                { key: 'Service', label: 'Service', color: GOLD },
              ]}
              variant="stacked"
              height={240}
              formatY={money}
            />
          </div>
        </section>
      )}

      {canSeeSales && <ResearchBrief customerId={id} />}

      {/* ── Tabbed drill-in ──────────────────────────────────── */}
      <div className="border-b border-navy-100 flex gap-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={clsx('px-3 py-2 text-sm -mb-px border-b-2',
              tab === t.key ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:bg-panel')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Activity / Notes & Visits */}
      {tab === 'activity' && (
        <section className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {['', 'visit', 'call', 'email', 'quote', 'note'].map(f => (
              <button key={f || 'all'} onClick={() => setTouchFilter(f)}
                className={clsx('px-2.5 py-1 text-xs rounded',
                  touchFilter === f ? 'bg-navy text-white' : 'bg-panel text-navy')}>
                {f ? `${TOUCH_ICON[f]} ${f}` : 'All'}
              </button>
            ))}
          </div>
          {a.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {!a.isLoading && filteredNotes.length === 0 && (
            <div className="card p-4 text-muted text-sm">No {touchFilter || ''} activity yet.</div>
          )}
          {filteredNotes.map(n => (
            <Link key={n.id} to={`/notes/${n.id}`} className="block card p-3 hover:bg-panel">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-sm flex items-center flex-wrap gap-1">
                  <span>{TOUCH_ICON[n.touch_type] || '✏️'}</span>
                  <span>{n.touch_type || 'note'} · by {n.user_name}</span>
                  {n.visibility === 'private' && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] uppercase tracking-wide">🔒 Private</span>
                  )}
                </div>
                <div className="text-xs text-muted">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {n.transcript && (
                <div className="text-sm text-muted mt-1 line-clamp-3">
                  {n.structured?.summary || n.transcript.slice(0, 200)}
                </div>
              )}
              {n.attachment_count > 0 && (
                <div className="text-xs text-muted mt-1">📎 {n.attachment_count} attachment{n.attachment_count === 1 ? '' : 's'}</div>
              )}
            </Link>
          ))}
        </section>
      )}

      {/* Sales */}
      {tab === 'sales' && canSeeSales && <SalesHistory customerId={id} />}

      {/* Pipeline */}
      {tab === 'pipeline' && canSeeSales && (
        <section className="space-y-2">
          {pipeline.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {!pipeline.isLoading && (pipeline.data?.length ?? 0) === 0 && (
            <div className="card p-4 text-muted text-sm">No open opportunities for this customer.</div>
          )}
          {pipeline.data?.map(o => (
            <Link key={o.id} to={`/pipeline/${o.id}`} className="block card p-3 hover:bg-panel">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-sm">{o.title || o.product || '(opportunity)'}</div>
                <div className="text-sm font-semibold tabular-nums">{o.value_usd ? money(o.value_usd) : '—'}</div>
              </div>
              <div className="text-xs text-muted mt-1">
                {[o.domain, o.stage, o.expected_close_date ? `close ${new Date(o.expected_close_date).toLocaleDateString()}` : null]
                  .filter(Boolean).join(' · ')}
                {o.user_name && <span> · {o.user_name}</span>}
              </div>
            </Link>
          ))}

          {/* Won/lost — closed deals used to just disappear once a deal's
              stage moved to won or lost since this tab only fetched status=open. */}
          {!closedPipeline.isLoading && (closedPipeline.data?.length ?? 0) > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-muted pt-2">
                Won / Lost ({closedPipeline.data.length})
              </div>
              {closedPipeline.data.map(o => (
                <Link key={o.id} to={`/pipeline/${o.id}`} className="block card p-3 hover:bg-panel">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-sm">{o.title || o.product || '(opportunity)'}</div>
                    <div className="text-sm font-semibold tabular-nums">{o.value_usd ? money(o.value_usd) : '—'}</div>
                  </div>
                  <div className="text-xs text-muted mt-1">
                    <span className={clsx('font-medium', o.stage === 'won' ? 'text-emerald-700' : 'text-crimson')}>
                      {o.stage === 'won' ? 'Won' : 'Lost'}
                    </span>
                    {o.domain && <span> · {o.domain}</span>}
                    {o.closed_at && <span> · {new Date(o.closed_at).toLocaleDateString()}</span>}
                    {o.stage === 'lost' && o.lost_reason && <span> · {o.lost_reason}</span>}
                    {o.user_name && <span> · {o.user_name}</span>}
                  </div>
                </Link>
              ))}
            </>
          )}
        </section>
      )}

      {/* Fleet (#58) — the customer's owned units */}
      {tab === 'fleet' && canSeeSales && (
        <section className="space-y-2">
          {fleet.isLoading && <div className="text-muted text-sm">Loading…</div>}
          {!fleet.isLoading && (fleet.data?.length ?? 0) === 0 && (
            <div className="card p-4 text-muted text-sm">No units on file for this customer (from Excede/Procede).</div>
          )}
          {(fleet.data?.length ?? 0) > 0 && (
            <div className="card p-3 overflow-x-auto">
              <div className="text-xs text-muted mb-2">{fleet.data.length} unit{fleet.data.length === 1 ? '' : 's'} on file</div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted uppercase tracking-wider border-b">
                  <tr>
                    <th className="py-1 pr-3">Year</th>
                    <th className="py-1 pr-3">Make</th>
                    <th className="py-1 pr-3">Model</th>
                    <th className="py-1 pr-3">VIN</th>
                    <th className="py-1 pr-3">In service</th>
                    <th className="py-1 pr-3 text-right">Odom</th>
                    <th className="py-1 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.data.map(u => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="py-1 pr-3">{u.model_year || '—'}</td>
                      <td className="py-1 pr-3">{u.make || '—'}</td>
                      <td className="py-1 pr-3">{u.model || '—'}</td>
                      <td className="py-1 pr-3 font-mono text-[11px]">{u.vin || '—'}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">{u.date_in_service ? new Date(u.date_in_service).toLocaleDateString() : '—'}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{u.odom ? u.odom.toLocaleString() : '—'}</td>
                      <td className="py-1 pr-3">{u.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Follow-ups */}
      {tab === 'followups' && (
        <section className="space-y-2">
          <button className="btn-ghost text-sm" onClick={() => setCreatingFollowup(true)}>⏱ + New follow-up</button>
          {(a.data?.followups.length ?? 0) === 0 && <div className="card p-4 text-muted text-sm">None open.</div>}
          {a.data?.followups.map(f => (
            <div key={f.id} className="card p-3 text-sm flex items-baseline justify-between gap-3">
              <div>
                <div className="font-medium">{f.title}</div>
                <div className="text-xs text-muted">{f.status} · {f.urgency} · due {new Date(f.due_date).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* People + Excede notes */}
      {tab === 'people' && (
        <section className="space-y-3">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div className="section-eyebrow">Notes from Excede</div>
              {hasPermission('can_edit_customers') && !editingNotes && (
                <button className="text-xs text-navy hover:text-crimson"
                        onClick={() => { setNotesValue(cust.notes || ''); setEditingNotes(true); }}>Edit</button>
              )}
            </div>
            <div className="rule-crimson" />
            {editingNotes ? (
              <div className="mt-2 space-y-2">
                <textarea className="input min-h-[80px] text-sm w-full" value={notesValue}
                          onChange={e => setNotesValue(e.target.value)} autoFocus />
                {saveNotes.isError && (
                  <div className="text-crimson text-xs">{saveNotes.error?.response?.data?.error || 'Save failed'}</div>
                )}
                <div className="flex gap-2">
                  <button className="btn-primary text-sm" disabled={saveNotes.isPending}
                          onClick={() => saveNotes.mutate(notesValue)}>{saveNotes.isPending ? 'Saving…' : 'Save'}</button>
                  <button className="btn-ghost text-sm" onClick={() => setEditingNotes(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="text-sm mt-2 whitespace-pre-wrap">{cust.notes || '—'}</div>
            )}
          </div>

          {cust.contacts?.length > 0 ? cust.contacts.map(p => (
            <div key={p.id} className="card p-3">
              <div className="font-medium">{p.name} {p.is_primary && <span className="text-crimson text-xs">★</span>}</div>
              <div className="text-xs text-muted">{p.role}</div>
              <div className="text-sm mt-1">{[p.phone, p.email].filter(Boolean).join(' · ') || '—'}</div>
            </div>
          )) : <div className="card p-4 text-muted text-sm">No contacts on file.</div>}
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className={`card p-2 text-center ${warn ? 'ring-1 ring-crimson' : ''}`}>
      <div className={`text-lg font-semibold ${warn ? 'text-crimson' : 'text-navy'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}
